import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stepper,
  Step,
  StepLabel,
  Box,
  Typography,
  Card,
  CardActionArea,
  TextField,
  Slider,
} from '@mui/material';
import GridOnIcon from '@mui/icons-material/GridOn';
import LinearScaleIcon from '@mui/icons-material/LinearScale';
import AdjustIcon from '@mui/icons-material/Adjust';
import { useNavigate } from 'react-router-dom';

import { useCreateRubricMutation } from '../rubricsApi';

import type { ResourceParentLocator } from '@/shared/types/resource';
import type {
  RubricType,
  RubricContent,
  AnalyticRubricContent,
  HolisticRubricContent,
  SinglePointRubricContent,
  RubricPerformanceLevel,
  RubricCriterion,
  RubricCell,
} from '@/shared/types/rubric';
import { useAppSelector } from '@/shared/hooks/redux';
import { useCreateResourceMutation } from '@/features/resources/resourcesApi';

const STEPS = ['Choose Type', 'Configure', 'Create'];

const TYPE_OPTIONS: {
  type: RubricType;
  label: string;
  description: string;
  icon: React.ReactNode;
}[] = [
  {
    type: 'analytic',
    label: 'Analytic Rubric',
    description:
      'A grid of criteria rows and performance level columns. Each cell describes what performance looks like for that criterion at that level.',
    icon: <GridOnIcon sx={{ fontSize: 48 }} />,
  },
  {
    type: 'holistic',
    label: 'Holistic Rubric',
    description:
      'A set of performance levels, each with one overall description. Best for quick assessments like writing or portfolios.',
    icon: <LinearScaleIcon sx={{ fontSize: 48 }} />,
  },
  {
    type: 'single-point',
    label: 'Single-Point Rubric',
    description:
      'A list of criteria, each with a "proficient" description. Teachers annotate strengths and areas for growth. Popular in modern pedagogy.',
    icon: <AdjustIcon sx={{ fontSize: 48 }} />,
  },
];

const DEFAULT_LEVEL_LABELS = ['Beginning', 'Developing', 'Proficient', 'Advanced'];

function generateId(): string {
  return crypto.randomUUID();
}

function scaffoldContent(
  type: RubricType,
  _title: string,
  criteriaCount: number,
  levelCount: number
): RubricContent {
  const levels: RubricPerformanceLevel[] = Array.from({ length: levelCount }, (_, i) => ({
    id: generateId(),
    label: DEFAULT_LEVEL_LABELS[i] ?? `Level ${i + 1}`,
    position: (i + 1) * 1000,
    description: '',
  }));

  const criteria: RubricCriterion[] = Array.from({ length: criteriaCount }, (_, i) => ({
    id: generateId(),
    title: `Criterion ${i + 1}`,
    description: '',
    position: (i + 1) * 1000,
    standards: [],
  }));

  if (type === 'analytic') {
    const cells: RubricCell[] = [];
    for (const criterion of criteria) {
      for (const level of levels) {
        cells.push({ criterionId: criterion.id, levelId: level.id, description: '' });
      }
    }
    return {
      schemaVersion: '1',
      rubricType: 'analytic',
      standards: [],
      performanceLevels: levels,
      criteria,
      cells,
    } satisfies AnalyticRubricContent;
  }

  if (type === 'holistic') {
    return {
      schemaVersion: '1',
      rubricType: 'holistic',
      standards: [],
      performanceLevels: levels,
    } satisfies HolisticRubricContent;
  }

  return {
    schemaVersion: '1',
    rubricType: 'single-point',
    standards: [],
    criteria,
  } satisfies SinglePointRubricContent;
}

interface Props {
  open: boolean;
  onClose: () => void;
  /** Library mode: invoked when no attachTo is provided */
  onCreated?: (rubricId: string) => void;
  /**
   * Resource mode: when provided, creates a `resources` row with
   * `resource_kind = 'rubric'` parented at this locator instead of a
   * library `rubrics` row.
   */
  attachTo?: ResourceParentLocator;
}

const CreateRubricWizard: React.FC<Props> = ({ open, onClose, onCreated, attachTo }) => {
  const navigate = useNavigate();
  const user = useAppSelector((s) => s.auth.user);
  const [createRubric, { isLoading: isCreating }] = useCreateRubricMutation();
  const [createResource, { isLoading: isCreatingResource }] = useCreateResourceMutation();
  const isLoading = isCreating || isCreatingResource;

  const [activeStep, setActiveStep] = useState(0);
  const [rubricType, setRubricType] = useState<RubricType>('analytic');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [criteriaCount, setCriteriaCount] = useState(4);
  const [levelCount, setLevelCount] = useState(4);

  const reset = () => {
    setActiveStep(0);
    setRubricType('analytic');
    setTitle('');
    setDescription('');
    setCriteriaCount(4);
    setLevelCount(4);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleCreate = async () => {
    if (!user?.id) return;
    const content = scaffoldContent(rubricType, title, criteriaCount, levelCount);
    const finalTitle = title || `New ${rubricType} Rubric`;
    const finalDescription = description || null;

    if (attachTo) {
      const created = await createResource({
        ...attachTo,
        resource: {
          resourceKind: 'rubric',
          title: finalTitle,
          description: finalDescription,
          content: { rubric: content },
        },
      }).unwrap();
      navigate(`/curriculum/resources/${created.id}/rubric`);
    } else {
      const result = await createRubric({
        title: finalTitle,
        description: finalDescription,
        ownerId: user.id,
        content,
      }).unwrap();
      if (onCreated) {
        onCreated(result.id);
      } else {
        navigate(`/curriculum/rubrics/${result.id}`);
      }
    }
    handleClose();
  };

  const canProceed = activeStep === 0 ? true : activeStep === 1 ? true : true;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Create Rubric</DialogTitle>
      <DialogContent>
        <Stepper activeStep={activeStep} sx={{ mb: 3, mt: 1 }}>
          {STEPS.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {/* Step 0: Choose type */}
        {activeStep === 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {TYPE_OPTIONS.map((opt) => (
              <Card
                key={opt.type}
                variant={rubricType === opt.type ? 'elevation' : 'outlined'}
                sx={{
                  border: rubricType === opt.type ? 2 : 1,
                  borderColor: rubricType === opt.type ? 'primary.main' : 'divider',
                }}
              >
                <CardActionArea onClick={() => setRubricType(opt.type)} sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box
                      sx={{ color: rubricType === opt.type ? 'primary.main' : 'text.secondary' }}
                    >
                      {opt.icon}
                    </Box>
                    <Box>
                      <Typography variant="subtitle1" fontWeight="bold">
                        {opt.label}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {opt.description}
                      </Typography>
                    </Box>
                  </Box>
                </CardActionArea>
              </Card>
            ))}
          </Box>
        )}

        {/* Step 1: Configure */}
        {activeStep === 1 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <TextField
              label="Rubric Title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              fullWidth
              placeholder={`New ${rubricType} Rubric`}
            />
            <TextField
              label="Description (optional)"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              fullWidth
              multiline
              rows={2}
            />
            {(rubricType === 'analytic' || rubricType === 'single-point') && (
              <Box>
                <Typography gutterBottom>Number of Criteria: {criteriaCount}</Typography>
                <Slider
                  value={criteriaCount}
                  onChange={(_, v) => setCriteriaCount(v as number)}
                  min={1}
                  max={10}
                  step={1}
                  marks
                />
              </Box>
            )}
            {(rubricType === 'analytic' || rubricType === 'holistic') && (
              <Box>
                <Typography gutterBottom>Number of Performance Levels: {levelCount}</Typography>
                <Slider
                  value={levelCount}
                  onChange={(_, v) => setLevelCount(v as number)}
                  min={2}
                  max={6}
                  step={1}
                  marks
                />
              </Box>
            )}
          </Box>
        )}

        {/* Step 2: Confirm */}
        {activeStep === 2 && (
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <Typography variant="h6" gutterBottom>
              {title || `New ${rubricType} Rubric`}
            </Typography>
            {description && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {description}
              </Typography>
            )}
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3, mt: 2 }}>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Type
                </Typography>
                <Typography variant="body1" fontWeight="bold" sx={{ textTransform: 'capitalize' }}>
                  {rubricType}
                </Typography>
              </Box>
              {(rubricType === 'analytic' || rubricType === 'single-point') && (
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Criteria
                  </Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {criteriaCount}
                  </Typography>
                </Box>
              )}
              {(rubricType === 'analytic' || rubricType === 'holistic') && (
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Levels
                  </Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {levelCount}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        {activeStep > 0 && <Button onClick={() => setActiveStep((s) => s - 1)}>Back</Button>}
        {activeStep < STEPS.length - 1 ? (
          <Button
            variant="contained"
            disabled={!canProceed}
            onClick={() => setActiveStep((s) => s + 1)}
          >
            Next
          </Button>
        ) : (
          <Button variant="contained" disabled={isLoading} onClick={handleCreate}>
            {isLoading ? 'Creating...' : 'Create Rubric'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CreateRubricWizard;
